import React, { useState, useEffect } from 'react';
import { Plane, MapPin, Calendar, Users, Euro, Plus, Pencil, Trash2, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BookTravelModal from '../components/BookTravelModal';
import NewTravelModal from '../components/NewTravelModal';
import ViewBookingsModal from '../components/ViewBookingsModal';
import type { Tables } from '../lib/types';
import { supabase } from '../lib/supabase';

const SingleTravel = () => {
  const { user } = useAuth();
  const [selectedTravel, setSelectedTravel] = useState<Tables<'travel_packages'> | null>(null);
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isViewBookingsModalOpen, setIsViewBookingsModalOpen] = useState(false);
  const [editingTravel, setEditingTravel] = useState<Tables<'travel_packages'> | null>(null);
  const [travels, setTravels] = useState<Tables<'travel_packages'>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTravelPackages();
  }, []);

  const loadTravelPackages = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from('travel_packages')
        .select('*')
        .eq('status', 'active')
        .order('start_date');

      if (queryError) throw queryError;
      setTravels(data || []);
    } catch (err: any) {
      console.error('Error loading travel packages:', err);
      setError(err.message || 'Errore nel caricamento dei viaggi');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTravel = async (travelData: any) => {
    try {
      setLoading(true);
      setError(null);

      const travelPayload = {
        title: travelData.title,
        description: travelData.description,
        location: travelData.location,
        start_date: travelData.startDate,
        end_date: travelData.endDate,
        participants_range: travelData.participantsRange,
        price: parseFloat(travelData.price),
        image_url: travelData.imageUrl,
        status: 'active'
      };

      if (editingTravel) {
        const { error: updateError } = await supabase
          .from('travel_packages')
          .update(travelPayload)
          .eq('id', editingTravel.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('travel_packages')
          .insert([travelPayload]);

        if (insertError) throw insertError;
      }

      await loadTravelPackages();
      setIsNewModalOpen(false);
      setEditingTravel(null);
    } catch (err: any) {
      console.error('Error saving travel package:', err);
      setError(err.message || 'Errore nel salvataggio del viaggio');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (travelId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo viaggio?')) return;

    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('travel_packages')
        .delete()
        .eq('id', travelId);

      if (deleteError) throw deleteError;

      await loadTravelPackages();
    } catch (err: any) {
      console.error('Error deleting travel package:', err);
      setError(err.message || 'Errore nell\'eliminazione del viaggio');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\./g, '/');
  };

  const handleBooking = (travel: Tables<'travel_packages'>) => {
    setSelectedTravel(travel);
    setIsBookModalOpen(true);
  };

  const handleEdit = (travel: Tables<'travel_packages'>) => {
    setEditingTravel(travel);
    setIsNewModalOpen(true);
  };

  const handleViewBookings = (travel: Tables<'travel_packages'>) => {
    setSelectedTravel(travel);
    setIsViewBookingsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Viaggi per Single</h1>
          <p className="text-gray-600">Caricamento viaggi in corso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Viaggi per Single</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const totalTravels = travels.length;
  const upcomingTravels = travels.filter(t => new Date(t.start_date) >= new Date()).length;
  const totalBookings = travels.reduce((sum, travel) => sum + (travel.bookings_count || 0), 0);

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                <Plane className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Viaggi per Single</h1>
                <p className="text-white/90 text-lg">Esperienze di viaggio uniche per single</p>
              </div>
            </div>
            {user?.role === 'admin' && (
              <button
                onClick={() => {
                  setEditingTravel(null);
                  setIsNewModalOpen(true);
                }}
                className="bg-white text-rose-600 px-4 py-2 rounded-lg hover:bg-rose-50 transition-all flex items-center gap-2 text-sm font-medium shadow-lg"
              >
                <Plus className="h-4 w-4" />
                Nuovo Viaggio
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Viaggi</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalTravels}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Plane className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Prossimi Viaggi</p>
                  <p className="text-3xl font-bold text-white mt-1">{upcomingTravels}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Prenotazioni</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalBookings}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Destinazioni</p>
                  <p className="text-3xl font-bold text-white mt-1">{new Set(travels.map(t => t.location)).size}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {travels.map((travel) => (
          <div key={travel.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="relative h-48">
              <img 
                src={travel.image_url} 
                alt={travel.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-rose-600 font-semibold shadow-sm">
                €{travel.price}
              </div>
            </div>
            
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-2">{travel.title}</h3>
              <p className="text-gray-600 text-sm mb-4">{travel.description}</p>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="h-4 w-4 text-rose-500" />
                  <span>{travel.location}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4 text-rose-500" />
                  <span>{formatDate(travel.start_date)} - {formatDate(travel.end_date)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="h-4 w-4 text-rose-500" />
                  <span>{travel.participants_range} partecipanti</span>
                </div>
              </div>
              
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleBooking(travel)}
                  className="flex-1 bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Plane className="h-4 w-4" />
                  Prenota Ora
                </button>

                {(user?.role === 'admin' || user?.role === 'organizer') && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewBookings(travel)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                      title="Visualizza Prenotazioni"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    {user?.role === 'admin' && (
                      <>
                        <button
                          onClick={() => handleEdit(travel)}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-lg"
                          title="Modifica"
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(travel.id)}
                          className="p-2 text-rose-600 hover:text-rose-900 hover:bg-rose-100 rounded-lg"
                          title="Elimina"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedTravel && (
        <>
          <BookTravelModal
            isOpen={isBookModalOpen}
            onClose={() => {
              setIsBookModalOpen(false);
              setSelectedTravel(null);
            }}
            travelPackage={selectedTravel}
          />

          <ViewBookingsModal
            isOpen={isViewBookingsModalOpen}
            onClose={() => {
              setIsViewBookingsModalOpen(false);
              setSelectedTravel(null);
            }}
            travelPackage={selectedTravel}
          />
        </>
      )}

      <NewTravelModal
        isOpen={isNewModalOpen}
        onClose={() => {
          setIsNewModalOpen(false);
          setEditingTravel(null);
        }}
        onSubmit={handleNewTravel}
        initialData={editingTravel}
      />
    </div>
  );
};

export default SingleTravel;